import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import axios from 'axios';
import storage from 'src/utils/storageUtils';

function WhatsAppBroadcast({ clinicId }) {
  const theme = useTheme();
  
  // State
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    if (clinicId) {
      fetchApprovedTemplates();
      fetchPatients();
    }
  }, [clinicId]);

  const fetchApprovedTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const token = storage.get('TOKEN');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/template/list?limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Only show templates that are APPROVED by Meta (or all if we don't strictly enforce yet for dev)
      const approved = (response.data.data || []).filter(t => t.metaStatus === 'APPROVED' || t.metaStatus === 'NOT_SUBMITTED' || t.status === 'ACTIVE');
      setTemplates(approved);
    } catch (error) {
      console.error("Failed to fetch templates", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchPatients = async () => {
    try {
      setLoadingPatients(true);
      const token = storage.get('TOKEN');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/patients?clinic=${clinicId}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPatients(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch patients", error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = patients.map((n) => n.id);
      setSelectedPatients(newSelecteds);
      return;
    }
    setSelectedPatients([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedPatients.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedPatients, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedPatients.slice(1));
    } else if (selectedIndex === selectedPatients.length - 1) {
      newSelected = newSelected.concat(selectedPatients.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedPatients.slice(0, selectedIndex),
        selectedPatients.slice(selectedIndex + 1),
      );
    }
    setSelectedPatients(newSelected);
  };

  const handleSendBroadcast = async () => {
    if (!selectedTemplate || selectedPatients.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to send this broadcast to ${selectedPatients.length} patients?`)) {
      return;
    }

    try {
      setSending(true);
      const token = storage.get('TOKEN');
      
      const payload = {
        templateId: selectedTemplate,
        patientIds: selectedPatients,
        clinicId: clinicId
      };

      await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/whatsapp/broadcast`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Broadcast campaign started successfully!');
      setSelectedPatients([]);
      setSelectedTemplate('');
      
    } catch (error) {
      console.error("Failed to send broadcast", error);
      alert(error.response?.data?.message || 'Failed to start broadcast');
    } finally {
      setSending(false);
    }
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Broadcast Campaigns</Typography>
          <Typography variant="body2" color="textSecondary">Send bulk WhatsApp messages to your patients</Typography>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} 
          disabled={!selectedTemplate || selectedPatients.length === 0 || sending}
          onClick={handleSendBroadcast}
        >
          {sending ? 'Sending...' : `Send to ${selectedPatients.length} Patients`}
        </Button>
      </Box>

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        
        {/* Left Column: Configuration */}
        <Box flex={1} display="flex" flexDirection="column" gap={3}>
          
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: theme.shadows[1] }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CampaignIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">1. Select Message Template</Typography>
            </Box>
            
            {loadingTemplates ? (
              <CircularProgress size={24} />
            ) : (
              <TextField 
                select 
                label="Choose an Approved Template" 
                fullWidth 
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                {templates.map(option => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.title} {option.metaCategory ? `(${option.metaCategory}${option.language ? ` • ${option.language}` : ''})` : ''}
                  </MenuItem>
                ))}
                {templates.length === 0 && (
                  <MenuItem disabled value="">No approved templates found</MenuItem>
                )}
              </TextField>
            )}
            
            {selectedTemplateData && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This template is marked as {selectedTemplateData.metaCategory || 'UTILITY'} and is ready to send.
              </Alert>
            )}
          </Card>

          <Card sx={{ p: 3, borderRadius: 3, boxShadow: theme.shadows[1], flexGrow: 1 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="h6" fontWeight="bold">2. Select Audience</Typography>
            </Box>
            
            <TableContainer sx={{ maxHeight: 400, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedPatients.length > 0 && selectedPatients.length < patients.length}
                        checked={patients.length > 0 && selectedPatients.length === patients.length}
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell>Patient Name</TableCell>
                    <TableCell>Mobile Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingPatients ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell>
                    </TableRow>
                  ) : patients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}><Typography color="textSecondary">No patients found</Typography></TableCell>
                    </TableRow>
                  ) : (
                    patients.map((row) => {
                      const isItemSelected = selectedPatients.indexOf(row.id) !== -1;
                      return (
                        <TableRow
                          hover
                          onClick={(event) => handleClick(event, row.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.id}
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} />
                          </TableCell>
                          <TableCell fontWeight="500">{row.fullName || row.name || 'Unknown'}</TableCell>
                          <TableCell>{row.contactNo || row.mobile || row.phone || 'N/A'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {selectedPatients.length} selected out of {patients.length} patients
            </Typography>
          </Card>

        </Box>

        {/* Right Column: Preview */}
        <Box width={{ xs: '100%', md: '350px' }}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: theme.shadows[1], bgcolor: '#f8f9fa', height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Message Preview</Typography>
            
            <Box bgcolor="#efeae2" p={2} borderRadius={3} sx={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: 'contain', border: `8px solid #333`, minHeight: '300px' }}>
              {selectedTemplateData ? (
                <Box bgcolor="#fff" p={1.5} borderRadius={2} boxShadow={1} position="relative">
                  <Typography variant="body2" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word' }}>
                    {selectedTemplateData.text}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '10px' }}>
                    10:30 AM
                  </Typography>
                </Box>
              ) : (
                <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="body2" color="textSecondary" align="center" bgcolor="rgba(255,255,255,0.7)" p={1} borderRadius={1}>
                    Select a template to see preview
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Box>

      </Box>
    </Box>
  );
}

export default WhatsAppBroadcast;
